import { forwardRef } from "react";
import { cx } from "cva";
import { ToastContainer, toast } from "react-toastify";
import type {
  CloseButtonProps,
  Id,
  ToastContainerProps,
  ToastContent,
  ToastOptions,
} from "react-toastify";
import type { FC, MouseEvent as ReactMouseEvent, PropsWithChildren, ReactNode } from "react";
import type { Merge } from "type-fest";
import { CloseIcon } from "@knitto-ui/react-icons";
import Button, { type ButtonIconProps } from "../Button";

export type SnackbarProps = ToastContainerProps;

export type SnackbarCloseButtonProps = Merge<ButtonIconProps, CloseButtonProps>;

export const SnackbarCloseButton = forwardRef<HTMLButtonElement, SnackbarCloseButtonProps>(
  (
    {
      ariaLabel,
      closeToast,
      type: _type,
      theme: _theme,
      className,
      color = "tertiary",
      size = "small",
    },
    ref,
  ) => (
    <Button.Icon
      ref={ref}
      className={cx("kn-snackbar-close", className)}
      onClick={(e: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (e) {
          e.stopPropagation?.();
          closeToast?.(e);
        }
      }}
      color={color}
      size={size}
      aria-label={ariaLabel}
    >
      <CloseIcon fill="rgb(255,255,255)" width={24} height={24} />
    </Button.Icon>
  ),
);

SnackbarCloseButton.displayName = "SnackbarCloseButton";

export const SnackbarContainer: FC<SnackbarProps> = ({
  draggable = false,
  hideProgressBar = true,
  position = "bottom-right",
  theme = "dark",
  toastClassName,
  bodyClassName,
  closeButton = (args) => <SnackbarCloseButton {...args} />,
  ...props
}) => {
  return (
    <ToastContainer
      draggable={draggable}
      hideProgressBar={hideProgressBar}
      position={position}
      theme={theme}
      toastClassName={cx("kn-snackbar-toastify", toastClassName)}
      bodyClassName={cx("kn-snackbar-body", bodyClassName)}
      closeButton={closeButton}
      {...props}
    />
  );
};

type WithActionProps = {
  action?: ReactNode;
  actionCallback?: () => void;
};

type SnackContentProps = PropsWithChildren<WithActionProps>;

const SnackContent: FC<SnackContentProps> = ({ action, actionCallback, children }) => {
  return (
    <div className="kn-snackbar-content">
      {children}
      {action ? (
        <Button
          className="kn-snackbar-action"
          color="tertiary"
          onClick={(e: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => {
            if (e) {
              e.stopPropagation?.();
              actionCallback?.();
            }
          }}
        >
          {action}
        </Button>
      ) : null}
    </div>
  );
};

export type SnackOptions = ToastOptions & WithActionProps;

export function snack<TData = unknown>(content: ToastContent<TData>, options?: SnackOptions): Id {
  // TODO: work on colors option
  const { action, actionCallback, ...restOptions } = options || {};
  return toast<TData>((props) => {
    return (
      <SnackContent action={action} actionCallback={actionCallback}>
        {typeof content === "function" ? content(props) : content}
      </SnackContent>
    );
  }, restOptions);
}
